import { Injectable, computed, inject, OnDestroy } from '@angular/core';
import { GraphQLClientService } from './graphql-client.service';
import { DelegateEmployee } from './delegation.service';
import { createApiState } from '../models/api-state';
import { errorFromException } from '../models/api-result';

const DELEGATE_EMPLOYEES_QUERY = `
  query delegateEmployees($search: String, $locationId: ID, $take: Int) {
    delegateEmployees(search: $search, locationId: $locationId, take: $take) {
      _id name firstName lastName email picture department jobTitle
    }
  }
`;

/**
 * RESPONSIBILITY: Search employees for booking delegation.
 *
 * SIGNALS:
 *   results — current search results (DelegateEmployee[])
 *   searching — true while query is in flight
 *
 * USED BY: DelegatePickerDialogComponent
 *
 * BACKEND CONTRACT: delegateEmployees(search, locationId, skip, take) query
 *   on rdx-platform-api. Gated by booking:create:on-behalf action.
 */
@Injectable({ providedIn: 'root' })
export class DelegateSearchService implements OnDestroy {
  private readonly graphql = inject(GraphQLClientService);

  // One controller rather than three hand-rolled signals: a triple can express
  // { loading: true, error, data } all at once, which is not a state this
  // search can actually be in.
  private readonly state = createApiState<DelegateEmployee[]>([]);
  /** Never null — the picker maps over this directly. */
  readonly results = computed(() => this.state.data() ?? []);
  readonly searching = this.state.loading;
  readonly error = this.state.error;
  private generation = 0;
  private timer?: ReturnType<typeof setTimeout>;

  ngOnDestroy(): void { this.clear(); }

  /** Empty queries browse eligible users; cancelled responses cannot repopulate the picker. */
  search(term: string, locationId: string, favourites?: Array<{ id: string; email: string }>): void {
    this.clear();
    const query = term.trim();
    if (query.length === 1) return;
    const generation = this.generation;
    this.state.setLoading();
    this.timer = setTimeout(async () => {
      try {
        // Resolve favourites through the permission-aware endpoint too. Never
        // treat membership in a personal group as delegation permission.
        const results = favourites
          ? (await Promise.all(favourites.map(async person => {
              const eligible = await this.executeSearch(person.email, locationId);
              return eligible.filter(e => e.id === person.id
                && `${e.name} ${e.email}`.toLowerCase().includes(query.toLowerCase()));
            }))).flat()
          : await this.executeSearch(query, locationId);
        if (generation === this.generation) this.state.setData(results);
      } catch (e) {
        if (generation === this.generation) this.state.setError(errorFromException(e));
      }
    }, query ? 250 : 0);
  }

  clear(): void {
    ++this.generation;
    clearTimeout(this.timer);
    this.state.reset();
    this.state.setData([]);
  }

  private async executeSearch(term: string, locationId: string): Promise<DelegateEmployee[]> {
    const employees = await this.graphql.query<Array<{
      _id: string;
      name?: string;
      firstName?: string;
      lastName?: string;
      email: string;
      picture?: string | null;
    }>>(DELEGATE_EMPLOYEES_QUERY, {
      search: term,
      locationId,
      take: 20,
    });

    if (!employees || !Array.isArray(employees)) return [];

    return employees.map((e) => ({
      id: e._id,
      name: e.name || `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim(),
      email: e.email,
      avatarUrl: e.picture ?? undefined,
    }));
  }
}
